import { useState, useEffect } from "react";
import type { Position } from "./types";

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function usePositions(user: string, trip: string) {
  const [positions, setPositions] = useState<Position[]>([]);
  const [status, setStatus] = useState("Loading…");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setStatus("Loading positions…");
        const resp = await fetch(`/api/${user}/${trip}/positions`);
        if (!resp.ok) throw new Error(`Failed to fetch positions (${resp.status})`);
        const rows: Omit<Position, "distanceM">[] = await resp.json();

        if (cancelled) return;

        let cumDist = 0;
        const withDist: Position[] = rows.map((r, i) => {
          if (i > 0) {
            cumDist += haversineM(
              rows[i - 1]!.latitude, rows[i - 1]!.longitude,
              r.latitude, r.longitude
            );
          }
          return { ...r, distanceM: cumDist };
        });

        setPositions(withDist);

        if (withDist.length) {
          const first = withDist[0]!;
          const last = withDist[withDist.length - 1]!;
          const km = Math.round(cumDist / 100) / 10;
          setStatus(
            `${withDist.length} positions · ${km} km · ` +
            `${new Date(first.timestamp).toLocaleDateString()} – ${new Date(last.timestamp).toLocaleDateString()}`
          );
        } else {
          setStatus("No positions yet.");
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : String(err);
          setError(msg);
          setStatus(`Error: ${msg}`);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user, trip]);

  return { positions, status, error };
}
